import React, { useState, useEffect, useRef, useCallback } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import * as Toast from '@radix-ui/react-toast';
import { CheckCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';

// --- CVA variants ---

const toastVariants = cva(
  'flex items-center gap-4 px-6 py-4 rounded-xl shadow-2xl z-50',
  {
    variants: {
      variant: {
        success: 'bg-gray-900 text-white',
        warning: 'bg-amber-900 text-amber-50',
        info: 'bg-blue-900 text-blue-50',
      },
    },
    defaultVariants: {
      variant: 'success',
    },
  }
);

const iconMap = {
  success: CheckCircle,
  warning: AlertTriangle,
  info: Info,
} as const;

const iconColorMap = {
  success: 'text-green-400',
  warning: 'text-amber-400',
  info: 'text-blue-400',
} as const;

// --- Props ---

export interface UndoToastProps extends VariantProps<typeof toastVariants> {
  open: boolean;
  message: string;
  onUndo: () => void;
  onDismiss: () => void;
  countdownSeconds?: number;
  className?: string;
}

// --- Component ---

export function UndoToast({
  open,
  message,
  onUndo,
  onDismiss,
  countdownSeconds = 10,
  variant = 'success',
  className,
}: UndoToastProps) {
  const { t } = useTranslation(['leaves', 'common']);
  const resolvedVariant = variant ?? 'success';
  const [countdown, setCountdown] = useState(countdownSeconds);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Reset countdown when toast opens
  useEffect(() => {
    if (open) {
      setCountdown(countdownSeconds);
      clearTimer();
      timerRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearTimer();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearTimer();
    }
    return clearTimer;
  }, [open, countdownSeconds, clearTimer]);

  // Auto-dismiss when countdown reaches 0
  useEffect(() => {
    if (open && countdown === 0) {
      onDismiss();
    }
  }, [countdown, open, onDismiss]);

  const IconComponent = iconMap[resolvedVariant];

  return (
    <Toast.Provider swipeDirection="down" duration={countdownSeconds * 1000}>
      <Toast.Root
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) onDismiss();
        }}
        data-testid="undo-toast"
        className={cn(toastVariants({ variant: resolvedVariant }), className)}
      >
        <IconComponent size={20} className={cn('flex-shrink-0', iconColorMap[resolvedVariant])} />

        <Toast.Description className="text-sm font-medium">
          {message}
        </Toast.Description>

        <Toast.Action asChild altText={t('common:Undo')}>
          <button
            onClick={onUndo}
            data-testid="undo-btn"
            className="px-3 py-1.5 text-sm font-semibold bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            {t('common:Undo')} ({countdown}s)
          </button>
        </Toast.Action>

        <Toast.Close asChild>
          <button
            onClick={onDismiss}
            data-testid="undo-toast-close"
            className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
          >
            <X size={16} />
          </button>
        </Toast.Close>
      </Toast.Root>

      <Toast.Viewport className="fixed bottom-8 ltr:left-1/2 rtl:right-1/2 ltr:-translate-x-1/2 rtl:translate-x-1/2 z-50 outline-none" />
    </Toast.Provider>
  );
}

export default UndoToast;
